"use client";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { CreditCard, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton";
import { useAppDispatch, useAppSelector } from "../hooks/redux/useRedux";
import { selectCheckPayment } from "../redux/slices/paymentSlice/PaymentSlice";
import { StoriesModal } from "../components/modals/storiesModal/StoriesModal";
import {
  selectStories,
  selectSocialProfile,
} from "../redux/slices/profileSlice/ProfileSlice";
import { getSocialProfileThunk } from "../redux/slices/profileSlice/asyncthunk";
import { ConnectWhatsAppModal } from "../components/modals/connectWhatsAppModal";
import { CustomAlertModal } from "../components/CustomAlertModal";

export default function NewsUpdates() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showSubModal, setShowSubModal] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
  const [showAlreadyConnectedModal, setShowAlreadyConnectedModal] = useState(false);
  const [index, setIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const stories = useAppSelector(selectStories);
  const socialProfile = useAppSelector(selectSocialProfile);
  const isWhatsAppConnected = Boolean(socialProfile?.whatsapp);

  useEffect(() => {
    if (!socialProfile?.whatsapp && !socialProfile?.instagram) {
      dispatch(getSocialProfileThunk());
    }
  }, [dispatch]);

  const timerRef = useRef(null);
  const navigate = useNavigate();

  const nextSlide = () =>
    setIndex((prev) => (prev + 2 < stories.length ? prev + 2 : 0));

  const prevSlide = () =>
    setIndex((prev) => (prev - 2 >= 0 ? prev - 2 : stories.length - 2));

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, [selectedIndex]);

  const currentStory = selectedIndex !== null ? stories[selectedIndex] : null;

  return (
    <div className="rounded-xl bg-white shadow p-6 w-full h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-neutral-800">
          {t("home.newsAndUpdates")}
        </h2>

        <div className="flex gap-4">
          <PrimaryButton
            onClick={prevSlide}
            label={<ChevronLeftIcon className="w-5 h-5" />}
            size="small"
            className="w-8 h-8 !p-0 rounded-full bg-[#020637] hover:bg-[#0e1c7a] text-white"
          />

          <PrimaryButton
            onClick={nextSlide}
            label={<ChevronRightIcon className="w-5 h-5" />}
            size="small"
            className="w-8 h-8 !p-0 rounded-full bg-[#020637] hover:bg-[#0e1c7a] text-white"
          />
        </div>
      </div>

      <div className="flex-grow min-h-0">
        {stories?.length === 0 ? (
          <div className="flex h-full gap-4 px-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="w-1/2 h-full rounded-xl bg-neutral-100 animate-pulse relative overflow-hidden flex flex-col justify-between p-3"
              >
                {/* Top header skeleton */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-neutral-200" />
                  <div className="w-24 h-3 rounded bg-neutral-200" />
                </div>
                {/* Bottom content skeleton */}
                <div className="space-y-2 mt-auto">
                  <div className="w-2/3 h-4 rounded bg-neutral-200" />
                  <div className="w-full h-3 rounded bg-neutral-200" />
                  <div className="w-5/6 h-3 rounded bg-neutral-200" />
                </div>
              </div>
            ))}
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center text-neutral-400 py-10">
            {t("home.noNewsAvailable")}
          </div>
        ) : (
          <div className="overflow-hidden relative h-full">
            <motion.div
              className="flex h-full"
              animate={{ x: `-${(index / 2) * 100}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            >
              {stories.map((item, idx) => {
                const firstItem = item.items?.[0];
                const displayImg =
                  firstItem?.background_image || item.icon_image || "";
                const displayDesc = firstItem?.description || "";
                const slideTitle = firstItem?.title || item.title;

                return (
                  <div
                    key={item.id}
                    className="w-1/2 flex-shrink-0 px-2 h-full"
                  >
                    <div
                      onClick={() => setSelectedIndex(idx)}
                      className="rounded-xl overflow-hidden shadow h-full cursor-pointer group relative"
                    >
                      <img
                        src={displayImg}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                      />

                      {/* Story Header (Icon + Title) */}
                      <div className="absolute top-3 left-3 flex items-center gap-2 z-10 pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        <div className="w-8 h-8 rounded-full border border-white/60 p-0.5 bg-neutral-900/40 backdrop-blur-sm shadow flex items-center justify-center">
                          <img
                            src={item.icon_image}
                            alt={item.title}
                            className="w-full h-full object-cover rounded-full"
                          />
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                        <h3 className="text-white text-sm font-semibold">
                          {slideTitle}
                        </h3>
                        {displayDesc && (
                          <p className="text-white/80 text-xs mt-1 line-clamp-2">
                            {displayDesc}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        )}
      </div>
      <StoriesModal
        showStory={currentStory}
        setShowStory={setSelectedIndex}
        stories={stories}
        hasSubscription={paymentCheck?.has_active_subscription}
        setShowSubModal={setShowSubModal}
        setIsWhatsAppModalOpen={(open) => {
          if (open && isWhatsAppConnected) {
            setShowAlreadyConnectedModal(true);
            return;
          }
          setIsWhatsAppModalOpen(open);
        }}
      />

      {!isWhatsAppConnected && (
        <ConnectWhatsAppModal
          isOpen={isWhatsAppModalOpen}
          onClose={() => setIsWhatsAppModalOpen(false)}
        />
      )}

      <CustomAlertModal
        isOpen={showAlreadyConnectedModal}
        onClose={() => setShowAlreadyConnectedModal(false)}
        onDonePress={() => setShowAlreadyConnectedModal(false)}
        text={t(
          "connectWhatsApp.alreadyConnected",
          "WhatsApp is already connected.",
        )}
        onSingleButton={true}
        doneText={t("common.ok", "OK")}
      />

      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <PrimaryButton
                onClick={() => setShowSubModal(false)}
                label={<X size={18} />}
                size="small"
                variant="ghost"
                className="absolute top-4 right-4 !p-1 text-gray-400 hover:text-gray-600"
              />

              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>

              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                {t("home.subscriptionRequired")}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {t("home.subscriptionRequiredDesc")}
              </p>

              <div className="flex gap-3">
                <PrimaryButton
                  onClick={() => setShowSubModal(false)}
                  label={t("home.cancel")}
                  variant="outline"
                  size="small"
                  className="flex-1 justify-center rounded-xl"
                />
                <PrimaryButton
                  onClick={() => {
                    setShowSubModal(false);
                    navigate("/settings?tab=subscription");
                  }}
                  label={t("home.viewPlans")}
                  size="small"
                  className="flex-1 justify-center rounded-xl bg-gradient-to-r from-[#020637] to-[#1e3aa0] hover:opacity-90"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
